import React from 'react';
import autoBind from 'react-autobind';
import { Alert, Button, Card, Select, Space, message, Modal, Tooltip } from 'antd';
import {
  CaretUpOutlined,
  CaretDownOutlined,
  ThunderboltFilled,
  CloudSyncOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import Utils from '../../../../../components/Utils';
import Globals from '../../../../../config/Globals';

export default class CourseCardTriggers extends React.Component {
  constructor(props) {
    super(props);
    autoBind(this);
    this.state = {
      isExpanded: false,
      blockedCourses: [],
      selectedCourses: [],
      coursesOptions: [],
    };
  }

  toggleCard() {
    this.setState((prevState) => ({ isExpanded: !prevState.isExpanded }));
  }

  handleCourseSelection(value) {
    this.setState({ selectedCourses: value });
  }

  async componentDidMount() {
    await this.getCoursesByCertification();
    this.loadBlockedCourses();
  }

  async loadBlockedCourses() {
    await this.props.onReload();
    const blockedCourses = this.props.certProc?.blockedCourses || [];
    this.setState({ blockedCourses, selectedCourses: blockedCourses });
  }

  render() {
    const { isExpanded, selectedCourses, coursesOptions } = this.state;
    const { certProc } = this.props;
    const triggerState = certProc?.triggerState || {};
    const stage = certProc?.stage || 'NEW';
    const stateData = triggerState[stage] || {};
    const stateValue = stateData.state || 'N/A';
    const updatedOn = stateData.updatedOn ? Utils.getDateAndTimeOnUIFormatByTimestamp(stateData.updatedOn) : 'N/A';
    const requestResult = stateData.requestResult || [];
    const disableActions = certProc?.state === Globals.CertificationProcess_State.COMPLETED;

    return (
      <>
        <div style={{ position: 'sticky', top: 0, zIndex: 1000 }}>
          <Alert
            message={
              <>
                Trigger for Stage: <strong>{stage}</strong>
              </>
            }
            type="warning"
            icon={<ThunderboltFilled />}
            showIcon
            action={
              <Button onClick={this.toggleCard} type="link">
                {isExpanded ? <CaretUpOutlined /> : <CaretDownOutlined />}
              </Button>
            }
          />
        </div>

        {isExpanded && (
          <Card style={{ marginTop: 0 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', fontWeight: 'bold' }}>
              <div style={{ flex: 1 }}>
                <p>State: {stateValue}</p>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <p style={{ marginRight: '8px' }}>Blocked Courses:</p>
                  <Select
                    mode="multiple"
                    style={{ width: '400px' }}
                    placeholder="Select courses"
                    value={selectedCourses.length ? selectedCourses : undefined}
                    onChange={this.handleCourseSelection}
                    options={coursesOptions}
                    allowClear
                    disabled={disableActions}
                  />
                </div>
                <p>
                  Updated On: {updatedOn}{' '}
                  <Tooltip title="View Request Result">
                    <EyeOutlined
                      style={{ marginLeft: 8 }}
                      onClick={() => this.showRequestResult(requestResult)}
                      aria-label="View Request Result"
                    />
                  </Tooltip>
                </p>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start' }}>
                <Space>
                  <Button
                    type="primary"
                    icon={<CloudSyncOutlined />}
                    onClick={this.checkTriggerStatus}
                    disabled={disableActions}
                  >
                    Execute trigger
                  </Button>
                  <Button type="primary" onClick={this.handleBlockedCourses} disabled={disableActions}>
                    Save
                  </Button>
                </Space>
              </div>
            </div>
          </Card>
        )}
      </>
    );
  }

  // Show Request Result
  showRequestResult(requestResult) {
    Modal.info({
      title: 'Request Result',
      content: (
        <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{JSON.stringify(requestResult, null, 2)}</pre>
      ),
      width: 600,
    });
  }

  //API calls
  async getCoursesByCertification() {
    const { certProc } = this.props;
    try {
      const courses = await this.props.app.config.course.getCourses(certProc?.certificationID);
      if (courses.statusCode === 200) {
        const courseList = courses.body.courses.map((course) => ({
          label: course.displayName,
          value: course.id,
        }));
        this.setState({ coursesOptions: courseList });
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  }

  async checkTriggerStatus() {
    const { certProc } = this.props;

    Modal.confirm({
      title: 'Confirm operation',
      content: 'This will evaluate the trigger again. Confirm operation?',
      onOk: async () => {
        const resp = await this.props.app.api.certification.checkTriggerStatus(certProc.userID, certProc.id);
        if (resp.statusCode === 200) {
          message.warning('Trigger evaluated. Reload the page to see the updated results.');
          this.props.onReload();
        } else {
          this.props.app.alertController.showAPIErrorAlert(null, resp);
        }
      },
    });
  }

  async handleBlockedCourses() {
    const { selectedCourses } = this.state;
    const { certProc } = this.props;
    const stage = certProc?.stage || 'NEW';
    const triggerState = certProc?.triggerState || {};
    const stateData = triggerState[stage] || {};
    const stateValue = stateData.state || 'N/A';

    Modal.confirm({
      title: 'Confirm operation',
      content: 'Override the blocked courses list? Confirm operation?',
      onOk: async () => {
        const body = { value: stateValue, blockedCourses: selectedCourses };
        const resp = await this.props.app.api.certification.setTriggerValue(certProc.userID, certProc.id, body);

        if (resp.statusCode === 200) {
          message.success('Blocked courses updated successfully.');

          this.setState({ blockedCourses: selectedCourses, selectedCourses });
          this.loadBlockedCourses();
        } else {
          message.error('Failed to update blocked courses.');
          this.loadBlockedCourses();
        }
      },
    });
  }
}
